import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Clock, Loader2 } from 'lucide-react';

interface ManterFilaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numeroPedido: string;
  razaoSocial: string;
  isLoading: boolean;
  /** O que adiar custa a quem aperta (Triagem Criação: desconto na comissão). */
  aviso?: string | null;
  onConfirm: (dados: { justificativa: string; prd?: string }) => void;
}

/**
 * "Passar para o fim da fila" com justificativa OBRIGATÓRIA (padrão Manter das
 * triagens) + Nº do PRD para os casos de fora de carteira/aguardando protocolo.
 * A justificativa (e o PRD) viram Nota na timeline do pedido.
 */
export function ManterFilaDialog({
  open,
  onOpenChange,
  numeroPedido,
  razaoSocial,
  isLoading,
  aviso,
  onConfirm,
}: ManterFilaDialogProps) {
  const [justificativa, setJustificativa] = useState('');
  const [prd, setPrd] = useState('');

  useEffect(() => {
    if (!open) { setJustificativa(''); setPrd(''); }
  }, [open]);

  const podeConfirmar = justificativa.trim().length >= 10 && !isLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Passar para o fim da fila — Pedido {numeroPedido}
          </DialogTitle>
          <DialogDescription>
            {razaoSocial}
            <br />
            O pedido desce para o fim da fila e o próximo libera. Justifique o motivo
            (ex.: fibra aguardando liberação de HP, CNPJ fora da carteira aguardando
            PRD, móvel fora da carteira). Tudo fica registrado na timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="manter-justificativa" className="text-xs font-semibold">
              Justificativa (obrigatória)
            </Label>
            <Textarea
              id="manter-justificativa"
              placeholder="Descreva por que este pedido não pode ser criado agora (mínimo 10 caracteres)..."
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              rows={3}
              disabled={isLoading}
              className="resize-none"
            />
            <p className={`text-[10px] ${justificativa.trim().length < 10 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {justificativa.trim().length}/10 caracteres mínimos
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="manter-prd" className="text-xs font-semibold">
              Nº do PRD (quando aguardando protocolo — ex.: fora de carteira)
            </Label>
            <Input
              id="manter-prd"
              placeholder="Ex.: 723299135"
              value={prd}
              onChange={(e) => setPrd(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        {aviso && (
          <p role="note" className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[12px] font-semibold text-primary">
            {aviso}
          </p>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Voltar
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-primary text-white"
            disabled={!podeConfirmar}
            onClick={() => onConfirm({ justificativa: justificativa.trim(), prd: prd.trim() || undefined })}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Clock className="h-4 w-4 mr-2" />
                Passar p/ fim da fila
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
